import {
  dashboardIssueProvidersListResponseSchema,
  dashboardIssueProviderDetailResponseSchema,
  dashboardIssuesListResponseSchema,
  dashboardIssueDetailResponseSchema,
  dashboardListChannelEnvironmentProfilesResponseSchema,
  dashboardSourceControlRepositoriesSummaryResponseSchema,
  dashboardSourceControlDiscoverBranchesResponseSchema,
  dashboardIssueWorkspaceCreateResponseSchema,
} from "@arti/schemas/dashboard";
import { asRecord } from "@arti/utilities";
import { z } from "zod";

import type {
  EnvironmentProfileOption,
  IssueDetail,
  IssueListItem,
  IssueListPage,
  IssueProviderSummary,
  IssueWorkspaceLaunchRequest,
  IssueWorkspaceLaunchResponse,
  NormalizedIssueStatus,
  RepositoryBranch,
  RepositoryOption,
} from "./types";

const providersResponseSchema = z.object({
  providers: z
    .array(
      dashboardIssueProvidersListResponseSchema.shape.providers.element.pick({
        default_project: true,
        display_name: true,
        is_enabled: true,
        provider_id: true,
      }),
    )
    .nullish(),
});
const projectsResponseSchema = z.object({
  projects: z
    .array(
      dashboardIssueProviderDetailResponseSchema.shape.projects.element.pick({
        external_key: true,
        name: true,
      }),
    )
    .nullish(),
});
const issueSchema = dashboardIssuesListResponseSchema.shape.issues.element
  .omit({ parent_key: true })
  .partial({
    assignee: true,
    issue_type: true,
    normalized_status: true,
    priority: true,
    status: true,
    updated_at: true,
    url: true,
  })
  .extend({ normalized_status: z.string().nullish() });
const issuesResponseSchema = z.object({
  issues: z.array(issueSchema).nullish(),
  page: dashboardIssuesListResponseSchema.shape.page.partial().nullish(),
});
const issueDetailResponseSchema = z.object({
  issue: issueSchema.extend({
    comments: z
      .array(
        dashboardIssueDetailResponseSchema.shape.issue.shape.comments.element.pick(
          { author: true, body: true, created_at: true },
        ),
      )
      .nullish(),
    description:
      dashboardIssueDetailResponseSchema.shape.issue.shape.description,
    labels:
      dashboardIssueDetailResponseSchema.shape.issue.shape.labels.nullish(),
  }),
  suggested_channel_name:
    dashboardIssueDetailResponseSchema.shape.suggested_channel_name,
});
const profileSchema =
  dashboardListChannelEnvironmentProfilesResponseSchema.shape.profiles.element;
const resourceDefaultsSchema = profileSchema.shape.resources
  .unwrap()
  .shape.defaults.pick({ cpuMilli: true, ramMb: true, workspaceMb: true });
const profilesResponseSchema = z.object({
  profiles: z
    .array(
      profileSchema
        .pick({
          displayName: true,
          profileId: true,
          selectable: true,
          unavailableMessage: true,
        })
        .extend({
          resources: z.object({ defaults: resourceDefaultsSchema }).nullable(),
        }),
    )
    .nullish(),
});
const repositoriesResponseSchema = z.object({
  repositories: z
    .array(
      dashboardSourceControlRepositoriesSummaryResponseSchema.shape.repositories.element
        .pick({
          default_branch: true,
          is_required: true,
          provider_id: true,
          remote_slug: true,
          repository_id: true,
        })
        .partial({ default_branch: true, is_required: true }),
    )
    .nullish(),
});
const branchesResponseSchema =
  dashboardSourceControlDiscoverBranchesResponseSchema.extend({
    branches:
      dashboardSourceControlDiscoverBranchesResponseSchema.shape.branches.nullish(),
  });

/** Thin typed fetch layer for the wizard.
 *
 * Same-origin endpoints proxied by the Vite dev server, matching the
 * hand-rolled hooks used elsewhere on this page (useTemplates). The launch
 * request/response are typed by the generated dashboard client; list/detail
 * mapping stays hand-rolled until the page migrates to TanStack Query.
 */

export class WizardApiError extends Error {
  readonly status: number;
  readonly code: string | null;
  readonly details: Record<string, unknown>;

  constructor(
    status: number,
    code: string | null,
    message: string,
    details: Record<string, unknown> = {},
  ) {
    super(message);
    this.name = "WizardApiError";
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

/** Friendly text for the channel-environment policy errors the launch can
 * return (raw codes otherwise leak into the dialog); null falls back to the
 * server-provided message. */
export function describeWizardErrorCode(error: WizardApiError): string | null {
  const detail = (key: string): string =>
    key in error.details ? String(error.details[key]) : "?";
  switch (error.code) {
    case "channel_environment_profile_resource_cap_exceeded": {
      return `Requested ${detail("field")} ${detail("requested")} exceeds this profile's cap of ${detail("cap")}.`;
    }
    case "channel_environment_profile_resource_defaults_required": {
      return `This profile requires the default ${detail("field")} of ${detail("required")} (requested ${detail("requested")}).`;
    }
    case "channel_environment_profile_not_selectable": {
      return "This sandbox profile is not available for new workspaces.";
    }
    case "channel_environment_egress_override_requires_admin": {
      return "Only org admins can change the network egress policy.";
    }
    case "channel_environment_policy_override_requires_admin": {
      return `Only org admins can set ${detail("field")} beyond ${detail("maximum")} (requested ${detail("requested")}).`;
    }
    case "channel_environment_profile_policy_unavailable": {
      return "The sandbox profile's policy could not be resolved — try again or pick another profile.";
    }
    case "channel_environment_profile_catalog_unavailable": {
      return "The sandbox profile catalog is unavailable right now — try again shortly.";
    }
    case "org_member_required": {
      return "You must be a member of this organization to launch a workspace.";
    }
    case "org_admin_required": {
      return "This operation requires an org admin.";
    }
    default: {
      return null;
    }
  }
}

async function requestJson<T>(
  input: string,
  schema: z.ZodType<T>,
  init?: RequestInit,
): Promise<T> {
  const res = await fetch(input, init);
  const body: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const record = asRecord(body) ?? {};
    const code = typeof record.error === "string" ? record.error : null;
    const message =
      typeof record.message === "string"
        ? record.message
        : (code ?? `Request failed (${res.status})`);
    throw new WizardApiError(res.status, code, message, record);
  }
  return schema.parse(body);
}

export async function listIssueProviders(): Promise<IssueProviderSummary[]> {
  const body = await requestJson(
    "/user/issue-integrations/providers",
    providersResponseSchema,
  );
  return (body.providers ?? []).map((provider) => ({
    defaultProject: provider.default_project,
    displayName: provider.display_name || provider.provider_id,
    enabled: provider.is_enabled,
    id: provider.provider_id,
  }));
}

export async function listProviderProjects(
  providerId: string,
): Promise<{ key: string; name: string }[]> {
  const body = await requestJson(
    `/user/issue-integrations/providers/${encodeURIComponent(providerId)}`,
    projectsResponseSchema,
  );
  return (body.projects ?? []).map((project) => ({
    key: project.external_key,
    name: project.name ?? project.external_key,
  }));
}

type RawIssue = z.infer<typeof issueSchema>;

function toNormalizedStatus(raw: unknown): NormalizedIssueStatus | null {
  return raw === "To Do" || raw === "In Progress" || raw === "Done"
    ? raw
    : null;
}

function toIssueListItem(raw: RawIssue): IssueListItem {
  return {
    assignee: raw.assignee ?? null,
    issueId: raw.issue_id,
    issueKey: raw.issue_key,
    issueType: raw.issue_type ?? null,
    normalizedStatus: toNormalizedStatus(raw.normalized_status),
    priority: raw.priority ?? null,
    projectKey: raw.project_key,
    providerId: raw.provider_id,
    status: raw.status ?? null,
    summary: raw.summary ?? "",
    updatedAt: raw.updated_at ?? null,
    url: raw.url ?? null,
  };
}

export async function listIssues(params: {
  providerId: string;
  projectKey?: string;
  q?: string;
  workState: "active" | "all";
  cursor?: string;
  limit?: number;
}): Promise<{ issues: IssueListItem[]; page: IssueListPage }> {
  const query = new URLSearchParams({ provider_id: params.providerId });
  if (params.projectKey) {
    query.set("project_key", params.projectKey);
  }
  if (params.q) {
    query.set("q", params.q);
  }
  query.set("work_state", params.workState);
  if (params.cursor) {
    query.set("cursor", params.cursor);
  }
  query.set("limit", String(params.limit ?? 20));
  const body = await requestJson(
    `/user/issues?${query.toString()}`,
    issuesResponseSchema,
  );
  return {
    issues: (body.issues ?? []).map(toIssueListItem),
    page: {
      hasMore: body.page?.has_more ?? false,
      nextCursor: body.page?.next_cursor ?? null,
    },
  };
}

export async function getIssueDetail(
  providerId: string,
  issueKey: string,
): Promise<IssueDetail> {
  const body = await requestJson(
    `/user/issues/${encodeURIComponent(providerId)}/${encodeURIComponent(issueKey)}`,
    issueDetailResponseSchema,
  );
  const raw = body.issue;
  return {
    ...toIssueListItem(raw),
    comments: (raw.comments ?? []).map((comment) => ({
      author: comment.author,
      body: comment.body,
      createdAt: comment.created_at,
    })),
    description: raw.description,
    labels: raw.labels ?? [],
    suggestedChannelName: body.suggested_channel_name,
  };
}

export async function listEnvironmentProfiles(): Promise<
  EnvironmentProfileOption[]
> {
  const body = await requestJson(
    "/user/channel-environment-profiles",
    profilesResponseSchema,
  );
  return (body.profiles ?? []).map((profile) => ({
    defaults: profile.resources
      ? {
          cpuCount: profile.resources.defaults.cpuMilli / 1000,
          ramMB: profile.resources.defaults.ramMb,
          storageMB: profile.resources.defaults.workspaceMb,
        }
      : null,
    displayName: profile.displayName,
    profileId: profile.profileId,
    selectable: profile.selectable,
    unavailableMessage: profile.unavailableMessage,
  }));
}

export async function listRepositories(): Promise<RepositoryOption[]> {
  const body = await requestJson(
    "/user/source-control/repositories/summary",
    repositoriesResponseSchema,
  );
  return (body.repositories ?? []).map((repo) => ({
    defaultBranch: repo.default_branch ?? null,
    displayName: repo.remote_slug ?? repo.repository_id,
    isRequired: repo.is_required ?? false,
    providerId: repo.provider_id,
    remoteSlug: repo.remote_slug ?? null,
    repositoryId: repo.repository_id,
  }));
}

/** Live remote branches for one repository (GitHub-backed). Fetched lazily,
 * only when the user opens the branch picker for an attached repo. */
export async function discoverBranches(
  providerId: string,
  repositorySlug: string,
): Promise<RepositoryBranch[]> {
  const body = await requestJson(
    `/user/source-control/providers/${encodeURIComponent(providerId)}/discover-branches?repositorySlug=${encodeURIComponent(repositorySlug)}`,
    branchesResponseSchema,
  );
  return (body.branches ?? []).map((branch) => ({
    isDefault: branch.is_default,
    name: branch.name,
  }));
}

/** Launch or re-attach: the same POST with the same idempotency key returns
 * the authoritative operation snapshot without re-executing side effects. */
export async function launchIssueWorkspace(
  request: IssueWorkspaceLaunchRequest,
): Promise<IssueWorkspaceLaunchResponse> {
  return requestJson(
    "/user/issue-workspaces",
    dashboardIssueWorkspaceCreateResponseSchema,
    {
      body: JSON.stringify(request),
      headers: { "Content-Type": "application/json" },
      method: "POST",
    },
  );
}
